import { watch } from "vue";
import { usePoll } from "@inertiajs/vue3";

const GRACE_POLL_TICKS = 5;

/**
 * Poll while a server-side flag is active, then through a bounded grace
 * window after it clears (GH-1018). Item details and the product photo
 * arrive from two separate queued jobs: `parsing` flips false when the
 * details job finishes, but the Cloudinary upload usually lands a few
 * seconds later. The grace window picks the photo up without a manual
 * refresh, while items that legitimately have no photo can't poll forever.
 *
 * Interim until the server exposes photo_pending (Backlog 86bbamrv2), at
 * which point the grace window can go back to a plain stop-on-flag poll.
 *
 * @param {() => boolean} isActive reads the flag from fresh page props
 * @param {{interval?: number, only: Array<string>, graceTicks?: number}} options
 * @returns {{start: Function, stop: Function}}
 */
export function useGracePoll(
    isActive,
    { interval = 2000, only, graceTicks = GRACE_POLL_TICKS },
) {
    let gracePollsRemaining = 0;

    const { start, stop } = usePoll(
        interval,
        {
            only,
            onSuccess: () => {
                if (isActive()) {
                    return;
                }

                if (gracePollsRemaining > 0) {
                    gracePollsRemaining -= 1;
                } else {
                    stop();
                }
            },
        },
        { autoStart: false, preserveState: true },
    );

    watch(
        isActive,
        (active) => {
            if (active) {
                gracePollsRemaining = graceTicks;
                start();
            }
        },
        { immediate: true },
    );

    return { start, stop };
}
